import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import PenIcon from 'assets/icons/pen.svg';
import MailingService, {
  CreateMailingSurveyCampaignRequest,
  CreateMailingSurveyRequest,
  MailingContact,
  MailingSurvey,
  MailingSurveyCampaign,
  MailingSurveyCampaignReportSummary,
  MailingSurveyQuestionReport,
  MailingSurveyRecipientReport,
  MailingTemplate,
  StartMailingSurveyCampaignRequest,
  UpdateMailingSurveyRequest,
} from '../../services/MailingService';
import {
  getCampaignStatusLabel,
  getMailingLogStatusLabel,
  getMailingSourceLabel,
  MAILING_CAMPAIGN_STATUS_OPTIONS,
} from './mailingLabels';

const { Text } = Typography;

type Props = {
  contacts: MailingContact[];
  templates: MailingTemplate[];
};

type SurveyStartFormValues = {
  startDate: dayjs.Dayjs;
  sendWindow: [dayjs.Dayjs, dayjs.Dayjs];
  responseDeadlineAt: dayjs.Dayjs;
};

type ContactSourceFilter = 'ALL' | 'MANUAL' | 'IMPORT';
type SurveyTabKey = 'content' | 'campaign';
type SurveyStatusFilter = 'ACTIVE' | 'PASSIVE';
type SurveyCampaignFilterStatus = 'DRAFT' | 'SENDING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

const DEFAULT_PAGE_SIZE = 10;
const normalizeText = (value?: string | null) => (value || '').trim().toLocaleLowerCase('tr-TR');
const SURVEY_CAMPAIGN_STATUS_OPTIONS =
  MAILING_CAMPAIGN_STATUS_OPTIONS as unknown as { label: string; value: SurveyCampaignFilterStatus }[];

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || fallback;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm:ss') : '-';
};

const formatTime = (value?: string | null) => {
  if (!value) return '-';
  return value.length >= 5 ? value.slice(0, 5) : value;
};

const formatTimeRange = (start?: string | null, end?: string | null) => {
  if (!start || !end) return '-';
  return `${formatTime(start)} - ${formatTime(end)}`;
};

const campaignStatusColor = (status?: string) => {
  if (status === 'DRAFT') return 'default';
  if (status === 'SENDING') return 'processing';
  if (status === 'PAUSED') return 'warning';
  if (status === 'COMPLETED') return 'success';
  if (status === 'CANCELLED') return 'error';
  return 'default';
};

const parseTimeToDayjs = (value?: string | null) => {
  if (!value) return null;
  const normalized = value.length === 5 ? `${value}:00` : value;
  const parsed = dayjs(`2000-01-01T${normalized}`);
  return parsed.isValid() ? parsed : null;
};

const normalizeQuestions = (questions: any[]) => {
  return (questions || []).map((question, questionIndex) => ({
    questionText: question?.questionText || '',
    questionType: question?.questionType || 'TEXT',
    required: question?.required !== false,
    orderNo: question?.orderNo ?? questionIndex,
    options:
      question?.questionType === 'MULTIPLE_CHOICE'
        ? (question?.options || []).map((option: any, optionIndex: number) => ({
            optionText: option?.optionText || '',
            isOther: option?.isOther === true,
            orderNo: option?.orderNo ?? optionIndex,
          }))
        : [],
  }));
};

const SurveyAdmin: React.FC<Props> = ({ contacts, templates }) => {
  const [loading, setLoading] = useState(false);
  const [surveys, setSurveys] = useState<MailingSurvey[]>([]);
  const [campaigns, setCampaigns] = useState<MailingSurveyCampaign[]>([]);
  const [reportSummary, setReportSummary] = useState<MailingSurveyCampaignReportSummary | null>(null);
  const [questionReport, setQuestionReport] = useState<MailingSurveyQuestionReport[]>([]);
  const [recipientReport, setRecipientReport] = useState<MailingSurveyRecipientReport[]>([]);

  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<MailingSurvey | null>(null);

  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [startCampaignTarget, setStartCampaignTarget] = useState<MailingSurveyCampaign | null>(null);

  const [isReportDrawerOpen, setIsReportDrawerOpen] = useState(false);
  const [reportCampaign, setReportCampaign] = useState<MailingSurveyCampaign | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [creatingSurvey, setCreatingSurvey] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [updatingSurveyStatusId, setUpdatingSurveyStatusId] = useState<string | null>(null);

  const [campaignContactSearch, setCampaignContactSearch] = useState('');
  const [campaignContactSourceFilter, setCampaignContactSourceFilter] = useState<ContactSourceFilter>('ALL');
  const [campaignTagFilters, setCampaignTagFilters] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<SurveyTabKey>('content');
  const [selectedSurveyStatusFilter, setSelectedSurveyStatusFilter] = useState<SurveyStatusFilter>('ACTIVE');
  const [pendingSurveyStatusFilter, setPendingSurveyStatusFilter] = useState<SurveyStatusFilter>('ACTIVE');
  const [surveyFilterOpen, setSurveyFilterOpen] = useState(false);
  const [surveySearchInput, setSurveySearchInput] = useState('');
  const [surveySearch, setSurveySearch] = useState('');
  const [surveyPage, setSurveyPage] = useState(1);
  const [surveyPageSize, setSurveyPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [campaignListSearchInput, setCampaignListSearchInput] = useState('');
  const [campaignListSearch, setCampaignListSearch] = useState('');
  const [campaignFilterOpen, setCampaignFilterOpen] = useState(false);
  const [selectedCampaignStatuses, setSelectedCampaignStatuses] = useState<SurveyCampaignFilterStatus[]>([]);
  const [pendingCampaignStatuses, setPendingCampaignStatuses] = useState<SurveyCampaignFilterStatus[]>([]);
  const [campaignListPage, setCampaignListPage] = useState(1);
  const [campaignListPageSize, setCampaignListPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [surveyForm] = Form.useForm<CreateMailingSurveyRequest>();
  const [campaignForm] = Form.useForm<CreateMailingSurveyCampaignRequest>();
  const [startForm] = Form.useForm<SurveyStartFormValues>();

  const selectedCampaignContactIds = Form.useWatch('contactIds', campaignForm) as string[] | undefined;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [surveyResp, campaignResp] = await Promise.all([
        MailingService.getSurveys(true),
        MailingService.getSurveyCampaigns(),
      ]);
      setSurveys(Array.isArray(surveyResp?.data) ? surveyResp.data : []);
      setCampaigns(Array.isArray(campaignResp?.data) ? campaignResp.data : []);
    } catch (error) {
      message.error(getErrorMessage(error, 'Anket verileri yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const activeContacts = useMemo(() => contacts.filter((contact) => contact.active !== false), [contacts]);
  const activeTemplates = useMemo(() => templates.filter((template) => template.active !== false), [templates]);
  const activeSurveys = useMemo(() => surveys.filter((survey) => survey.active !== false), [surveys]);
  const debouncedApplySurveySearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setSurveySearch(value);
      }, 300),
    [],
  );
  const debouncedApplyCampaignSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCampaignListSearch(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedApplySurveySearch.cancel();
      debouncedApplyCampaignSearch.cancel();
    };
  }, [debouncedApplyCampaignSearch, debouncedApplySurveySearch]);

  const filteredSurveyContents = useMemo(() => {
    const normalizedSearch = normalizeText(surveySearch);
    return surveys
      .filter((survey) => {
        if (selectedSurveyStatusFilter === 'ACTIVE') {
          return survey.active !== false;
        }
        return survey.active === false;
      })
      .filter((survey) => {
        if (!normalizedSearch) return true;
        const searchable = [survey.name, survey.description].map((value) => normalizeText(value)).join(' ');
        return searchable.includes(normalizedSearch);
      });
  }, [surveySearch, selectedSurveyStatusFilter, surveys]);

  const filteredSurveyCampaigns = useMemo(() => {
    const normalizedSearch = normalizeText(campaignListSearch);
    return campaigns
      .filter((campaign) => {
        if (!selectedCampaignStatuses.length) return true;
        return selectedCampaignStatuses.includes((campaign.status || 'DRAFT') as SurveyCampaignFilterStatus);
      })
      .filter((campaign) => {
        if (!normalizedSearch) return true;
        const searchable = [campaign.name, campaign.survey?.name, campaign.template?.name, campaign.status]
          .map((value) => normalizeText(value))
          .join(' ');
        return searchable.includes(normalizedSearch);
      });
  }, [campaignListSearch, campaigns, selectedCampaignStatuses]);

  useEffect(() => {
    setSurveyPage(1);
  }, [selectedSurveyStatusFilter, surveySearch]);

  useEffect(() => {
    setCampaignListPage(1);
  }, [campaignListSearch, selectedCampaignStatuses]);

  const campaignTagOptions = useMemo(() => {
    const tags = new Set<string>();
    activeContacts.forEach((contact) => (contact.tags || []).forEach((tag) => tags.add(tag)));
    return Array.from(tags).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [activeContacts]);

  const filteredCampaignContacts = useMemo(() => {
    const normalizedSearch = campaignContactSearch.trim().toLowerCase();
    return activeContacts.filter((contact) => {
      const source = contact.source || 'MANUAL';
      if (campaignContactSourceFilter !== 'ALL' && campaignContactSourceFilter !== source) {
        return false;
      }

      const tags = contact.tags || [];
      if (campaignTagFilters.length > 0 && !tags.some((tag) => campaignTagFilters.includes(tag))) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchable = [contact.firstName || '', contact.lastName || '', contact.email || '', contact.phone || '', contact.companyName || '']
        .join(' ')
        .toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [activeContacts, campaignContactSearch, campaignContactSourceFilter, campaignTagFilters]);

  const openCreateSurveyModal = () => {
    setEditingSurvey(null);
    surveyForm.resetFields();
    surveyForm.setFieldsValue({
      name: '',
      description: '',
      questions: [
        {
          questionText: '',
          questionType: 'TEXT',
          required: true,
          options: [],
        },
      ],
    });
    setIsSurveyModalOpen(true);
  };

  const openEditSurveyModal = (survey: MailingSurvey) => {
    setEditingSurvey(survey);
    surveyForm.resetFields();
    surveyForm.setFieldsValue({
      name: survey.name,
      description: survey.description || '',
      questions: normalizeQuestions(survey.questions || []),
    });
    setIsSurveyModalOpen(true);
  };

  const closeSurveyModal = () => {
    setIsSurveyModalOpen(false);
    setEditingSurvey(null);
    surveyForm.resetFields();
  };

  const handleSaveSurvey = async () => {
    try {
      setCreatingSurvey(true);
      const values = await surveyForm.validateFields();
      const payload = {
        ...values,
        questions: normalizeQuestions(values.questions || []),
      };

      if (editingSurvey) {
        await MailingService.updateSurvey(editingSurvey.id, payload as UpdateMailingSurveyRequest);
        message.success('Anket güncellendi');
      } else {
        await MailingService.createSurvey(payload);
        message.success('Anket eklendi');
      }
      closeSurveyModal();
      await loadData();
    } catch (error) {
      if (!(error as any)?.errorFields) {
        message.error(getErrorMessage(error, 'Anket kaydedilemedi'));
      }
    } finally {
      setCreatingSurvey(false);
    }
  };

  const handleSetSurveyStatus = async (surveyId: string, active: boolean) => {
    try {
      setUpdatingSurveyStatusId(surveyId);
      await MailingService.updateSurveyStatus(surveyId, active);
      message.success(active ? 'Anket aktife alındı' : 'Anket pasife alındı');
      await loadData();
    } catch (error) {
      message.error(getErrorMessage(error, 'Anket durumu güncellenemedi'));
    } finally {
      setUpdatingSurveyStatusId(null);
    }
  };

  const openCampaignModal = () => {
    campaignForm.resetFields();
    campaignForm.setFieldsValue({ delayMinutes: 0, contactIds: [] });
    setCampaignContactSearch('');
    setCampaignContactSourceFilter('ALL');
    setCampaignTagFilters([]);
    setIsCampaignModalOpen(true);
  };

  const closeCampaignModal = () => {
    setIsCampaignModalOpen(false);
    campaignForm.resetFields();
  };

  const handleCreateCampaign = async () => {
    try {
      setCreatingCampaign(true);
      const values = await campaignForm.validateFields();
      await MailingService.createSurveyCampaign(values);
      message.success('Anket gönderimi eklendi');
      closeCampaignModal();
      await loadData();
    } catch (error) {
      if (!(error as any)?.errorFields) {
        message.error(getErrorMessage(error, 'Anket gönderimi eklenemedi'));
      }
    } finally {
      setCreatingCampaign(false);
    }
  };

  const openStartModal = (campaign: MailingSurveyCampaign) => {
    const defaultWindowStart = dayjs().hour(9).minute(0).second(0);
    const defaultWindowEnd = dayjs().hour(18).minute(0).second(0);
    const scheduledStart = campaign.scheduledStartAt ? dayjs(campaign.scheduledStartAt) : null;

    setStartCampaignTarget(campaign);
    startForm.setFieldsValue({
      startDate: scheduledStart && scheduledStart.isValid() ? scheduledStart : dayjs(),
      sendWindow: [
        parseTimeToDayjs(campaign.sendWindowStartTime) || defaultWindowStart,
        parseTimeToDayjs(campaign.sendWindowEndTime) || defaultWindowEnd,
      ],
      responseDeadlineAt:
        campaign.responseDeadlineAt && dayjs(campaign.responseDeadlineAt).isValid()
          ? dayjs(campaign.responseDeadlineAt)
          : dayjs().add(7, 'day'),
    });
    setIsStartModalOpen(true);
  };

  const closeStartModal = () => {
    setIsStartModalOpen(false);
    setStartCampaignTarget(null);
    startForm.resetFields();
  };

  const handleStartCampaign = async () => {
    if (!startCampaignTarget) return;

    try {
      const values = await startForm.validateFields();
      const payload: StartMailingSurveyCampaignRequest = {
        startDate: values.startDate.format('YYYY-MM-DD'),
        sendWindowStartTime: values.sendWindow[0].format('HH:mm:ss'),
        sendWindowEndTime: values.sendWindow[1].format('HH:mm:ss'),
        responseDeadlineAt: values.responseDeadlineAt.format('YYYY-MM-DDTHH:mm:ss'),
      };
      await MailingService.startSurveyCampaign(startCampaignTarget.id, payload);
      message.success('Anket gönderimi başlatıldı');
      closeStartModal();
      await loadData();
    } catch (error) {
      if (!(error as any)?.errorFields) {
        message.error(getErrorMessage(error, 'Anket gönderimi başlatılamadı'));
      }
    }
  };

  const handleStopCampaign = async (campaignId: string) => {
    try {
      await MailingService.stopSurveyCampaign(campaignId);
      message.success('Anket gönderimi durduruldu');
      await loadData();
    } catch (error) {
      message.error(getErrorMessage(error, 'Anket gönderimi durdurulamadı'));
    }
  };

  const handleCancelCampaign = async (campaignId: string) => {
    try {
      await MailingService.cancelSurveyCampaign(campaignId);
      message.success('Anket gönderimi iptal edildi');
      await loadData();
    } catch (error) {
      message.error(getErrorMessage(error, 'Anket gönderimi iptal edilemedi'));
    }
  };

  const openReportDrawer = async (campaign: MailingSurveyCampaign) => {
    setIsReportDrawerOpen(true);
    setReportCampaign(campaign);
    setReportLoading(true);
    try {
      const [summaryResp, questionsResp, recipientsResp] = await Promise.all([
        MailingService.getSurveyReportSummary(campaign.id),
        MailingService.getSurveyReportQuestions(campaign.id),
        MailingService.getSurveyReportRecipients(campaign.id),
      ]);
      setReportSummary(summaryResp.data);
      setQuestionReport(Array.isArray(questionsResp.data) ? questionsResp.data : []);
      setRecipientReport(Array.isArray(recipientsResp.data) ? recipientsResp.data : []);
    } catch (error) {
      message.error(getErrorMessage(error, 'Anket raporu yüklenemedi'));
      setReportSummary(null);
      setQuestionReport([]);
      setRecipientReport([]);
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportDrawer = () => {
    setIsReportDrawerOpen(false);
    setReportCampaign(null);
    setReportSummary(null);
    setQuestionReport([]);
    setRecipientReport([]);
  };

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ width: 220 }}>
          <Menu
            mode='inline'
            selectedKeys={[activeTab]}
            items={[
              { key: 'content', label: 'Anket İçerikleri' },
              { key: 'campaign', label: 'Anket Gönderimleri' },
            ]}
            onClick={({ key }) => setActiveTab((key as SurveyTabKey) || 'content')}
          />
        </div>

        <div style={{ flex: 1 }}>
          {activeTab === 'content' && (
            <>
              <Card
                className='br-30px kontrolx-card'
                title={
                  <Row align='middle'>
                    <Text style={{ fontWeight: 600 }}>Anket Listesi</Text>
                    <Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
                      <strong>({filteredSurveyContents.length})</strong> kayıt listelendi
                    </Text>
                  </Row>
                }
                extra={
                  <Space>
                    <Input
                      allowClear
                      placeholder='Ara'
                      prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                      className='d-input d-input-form d-input-filter'
                      style={{ width: 220 }}
                      value={surveySearchInput}
                      onChange={(event) => {
                        const value = event.target.value || '';
                        setSurveySearchInput(value);
                        if (value.length >= 2) {
                          debouncedApplySurveySearch(value);
                        } else if (value.length === 0) {
                          debouncedApplySurveySearch('');
                        }
                      }}
                    />
                    <Button
                      type='primary'
                      onClick={() => {
                        setPendingSurveyStatusFilter(selectedSurveyStatusFilter);
                        setSurveyFilterOpen(true);
                      }}
                    >
                      Filtrele
                    </Button>
                    <Button type='primary' onClick={openCreateSurveyModal}>
                      Ekle
                    </Button>
                  </Space>
                }
              >
                <Table<MailingSurvey>
                  rowKey='id'
                  loading={loading}
                  dataSource={filteredSurveyContents}
                  pagination={{
                    current: surveyPage,
                    pageSize: surveyPageSize,
                    total: filteredSurveyContents.length,
                    onChange: (nextPage, nextPageSize) => {
                      setSurveyPage(nextPage);
                      setSurveyPageSize(nextPageSize || DEFAULT_PAGE_SIZE);
                    },
                  }}
                  columns={[
                    { title: 'Ad', dataIndex: 'name', key: 'name' },
                    {
                      title: 'Soru Sayısı',
                      key: 'questionCount',
                      render: (_: unknown, record) => (Array.isArray(record.questions) ? record.questions.length : 0),
                    },
                    {
                      title: 'Durum',
                      dataIndex: 'active',
                      key: 'active',
                      render: (active: boolean | undefined, record) => (
                        <Switch
                          checked={active !== false}
                          checkedChildren='AKTIF'
                          unCheckedChildren='PASIF'
                          loading={updatingSurveyStatusId === record.id}
                          onChange={(checked) => handleSetSurveyStatus(record.id, checked)}
                        />
                      ),
                    },
                    {
                      title: 'İşlemler',
                      key: 'actions',
                      render: (_: unknown, record) => (
                        <Space>
                          <Tooltip title='Düzenle'>
                            <Button
                              icon={<img src={PenIcon} alt='pen' className='icon' />}
                              shape='circle'
                              onClick={() => openEditSurveyModal(record)}
                              className='icon-button'
                            />
                          </Tooltip>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>

              <Drawer open={surveyFilterOpen} onClose={() => setSurveyFilterOpen(false)} destroyOnClose width={420} title='Filtre'>
                <Space direction='vertical' style={{ width: '100%' }} size='large'>
                  <div>
                    <Typography.Text>Durum</Typography.Text>
                    <Select<SurveyStatusFilter>
                      style={{ width: '100%', marginTop: 8 }}
                      value={pendingSurveyStatusFilter}
                      onChange={setPendingSurveyStatusFilter}
                      options={[
                        { label: 'Aktif', value: 'ACTIVE' },
                        { label: 'Pasif', value: 'PASSIVE' },
                      ]}
                    />
                  </div>

                  <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                    <Button onClick={() => setPendingSurveyStatusFilter('ACTIVE')}>Temizle</Button>
                    <Button
                      type='primary'
                      onClick={() => {
                        setSelectedSurveyStatusFilter(pendingSurveyStatusFilter);
                        setSurveyFilterOpen(false);
                      }}
                    >
                      Filtrele
                    </Button>
                  </Space>
                </Space>
              </Drawer>
            </>
          )}

          {activeTab === 'campaign' && (
            <>
              <Card
                className='br-30px kontrolx-card'
                title={
                  <Space align='center' size={12}>
                    <Text style={{ fontWeight: 600 }}>Anket Gönderim Listesi</Text>
                    <Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
                      <strong>({filteredSurveyCampaigns.length})</strong> kayıt listelendi
                    </Text>
                  </Space>
                }
                extra={
                  <Space>
                    <Input
                      allowClear
                      placeholder='Ara'
                      prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                      className='d-input d-input-form d-input-filter'
                      style={{ width: 220 }}
                      value={campaignListSearchInput}
                      onChange={(event) => {
                        const value = event.target.value || '';
                        setCampaignListSearchInput(value);
                        if (value.length >= 2) {
                          debouncedApplyCampaignSearch(value);
                        } else if (value.length === 0) {
                          debouncedApplyCampaignSearch('');
                        }
                      }}
                    />
                    <Button
                      type='primary'
                      onClick={() => {
                        setPendingCampaignStatuses(selectedCampaignStatuses);
                        setCampaignFilterOpen(true);
                      }}
                    >
                      Filtrele
                    </Button>
                    <Button type='primary' onClick={openCampaignModal}>
                      Ekle
                    </Button>
                  </Space>
                }
              >
                <Table<MailingSurveyCampaign>
                  rowKey='id'
                  loading={loading}
                  dataSource={filteredSurveyCampaigns}
                  pagination={{
                    current: campaignListPage,
                    pageSize: campaignListPageSize,
                    total: filteredSurveyCampaigns.length,
                    onChange: (nextPage, nextPageSize) => {
                      setCampaignListPage(nextPage);
                      setCampaignListPageSize(nextPageSize || DEFAULT_PAGE_SIZE);
                    },
                  }}
                  columns={[
                    { title: 'Ad', dataIndex: 'name', key: 'name' },
                    {
                      title: 'Anket',
                      key: 'survey',
                      render: (_: unknown, record) => record.survey?.name || '-',
                    },
                    {
                      title: 'Şablon',
                      key: 'template',
                      render: (_: unknown, record) => record.template?.name || '-',
                    },
                    { title: 'Gecikme (dk)', dataIndex: 'delayMinutes', key: 'delayMinutes' },
                    {
                      title: 'Başlangıç',
                      dataIndex: 'scheduledStartAt',
                      key: 'scheduledStartAt',
                      render: (v?: string | null) => formatDateTime(v),
                    },
                    {
                      title: 'Saat Aralığı',
                      key: 'sendWindow',
                      render: (_: unknown, record) => formatTimeRange(record.sendWindowStartTime, record.sendWindowEndTime),
                    },
                    {
                      title: 'Planlanan Bitiş',
                      dataIndex: 'plannedEndAt',
                      key: 'plannedEndAt',
                      render: (v?: string | null) => formatDateTime(v),
                    },
                    {
                      title: 'Yanıt Son Tarih',
                      dataIndex: 'responseDeadlineAt',
                      key: 'responseDeadlineAt',
                      render: (v?: string | null) => formatDateTime(v),
                    },
                    {
                      title: 'Durum',
                      dataIndex: 'status',
                      key: 'status',
                      render: (status?: string) => <Tag color={campaignStatusColor(status)}>{getCampaignStatusLabel(status)}</Tag>,
                    },
                    {
                      title: 'Kişi Sayısı',
                      key: 'recipientCount',
                      render: (_: unknown, record) => record.recipientCount ?? record.contactIds?.length ?? 0,
                    },
                    {
                      title: 'İşlem',
                      key: 'actions',
                      render: (_: unknown, record) => (
                        <Space size={4} wrap>
                          {(record.status === 'DRAFT' || record.status === 'PAUSED') && (
                            <Button type='link' style={{ padding: 0 }} onClick={() => openStartModal(record)}>
                              {record.status === 'PAUSED' ? 'Devam Et' : 'Başlat'}
                            </Button>
                          )}
                          {record.status === 'SENDING' && (
                            <Popconfirm
                              title='Anket gönderimi durdurulsun mu?'
                              okText='Onayla'
                              cancelText='Vazgeç'
                              onConfirm={() => handleStopCampaign(record.id)}
                            >
                              <Button type='link' style={{ padding: 0 }}>
                                Durdur
                              </Button>
                            </Popconfirm>
                          )}
                          {(record.status === 'DRAFT' || record.status === 'PAUSED') && (
                            <Popconfirm
                              title='Anket gönderimi iptal edilsin mi?'
                              okText='Onayla'
                              cancelText='Vazgeç'
                              onConfirm={() => handleCancelCampaign(record.id)}
                            >
                              <Button type='link' danger style={{ padding: 0 }}>
                                İptal
                              </Button>
                            </Popconfirm>
                          )}
                          <Button type='link' style={{ padding: 0 }} onClick={() => openReportDrawer(record)}>
                            Rapor
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>

              <Drawer open={campaignFilterOpen} onClose={() => setCampaignFilterOpen(false)} destroyOnClose width={420} title='Filtre'>
                <Space direction='vertical' style={{ width: '100%' }} size='large'>
                  <div>
                    <Typography.Text>Durum</Typography.Text>
                    <Select
                      mode='multiple'
                      allowClear
                      style={{ width: '100%', marginTop: 8 }}
                      value={pendingCampaignStatuses}
                      onChange={(values) => setPendingCampaignStatuses(values as SurveyCampaignFilterStatus[])}
                      placeholder='Durum seçiniz'
                      options={SURVEY_CAMPAIGN_STATUS_OPTIONS}
                    />
                  </div>

                  <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                    <Button onClick={() => setPendingCampaignStatuses([])}>Temizle</Button>
                    <Button
                      type='primary'
                      onClick={() => {
                        setSelectedCampaignStatuses(pendingCampaignStatuses);
                        setCampaignFilterOpen(false);
                      }}
                    >
                      Filtrele
                    </Button>
                  </Space>
                </Space>
              </Drawer>
            </>
          )}
        </div>
      </div>

      <Modal
        title={editingSurvey ? 'Anket Düzenle' : 'Anket Ekle'}
        open={isSurveyModalOpen}
        onCancel={closeSurveyModal}
        onOk={handleSaveSurvey}
        okText={editingSurvey ? 'Güncelle' : 'Kaydet'}
        okButtonProps={{ loading: creatingSurvey }}
        width={1100}
      >
        <Form layout='vertical' form={surveyForm}>
          <Form.Item name='name' label='Anket Adı' rules={[{ required: true, message: 'Anket adı zorunludur' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='description' label='Açıklama'>
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.List name='questions'>
            {(questionFields, { add: addQuestion, remove: removeQuestion }) => (
              <Space direction='vertical' size={12} style={{ width: '100%' }}>
                {questionFields.map((questionField, questionIndex) => (
                  <Card
                    key={questionField.key}
                    size='small'
                    title={`Soru ${questionIndex + 1}`}
                    extra={
                      <Button danger type='link' onClick={() => removeQuestion(questionField.name)}>
                        Soruyu Sil
                      </Button>
                    }
                  >
                    <Row gutter={12}>
                      <Col span={24}>
                        <Form.Item
                          name={[questionField.name, 'questionText']}
                          label='Soru Metni'
                          rules={[{ required: true, message: 'Soru metni zorunludur' }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item
                          name={[questionField.name, 'questionType']}
                          label='Soru Tipi'
                          rules={[{ required: true, message: 'Soru tipi zorunludur' }]}
                        >
                          <Select
                            options={[
                              { label: 'Text', value: 'TEXT' },
                              { label: 'Çoktan Seçmeli', value: 'MULTIPLE_CHOICE' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name={[questionField.name, 'required']} label='Zorunlu' valuePropName='checked'>
                          <Switch checkedChildren='Evet' unCheckedChildren='Hayır' />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={[questionField.name, 'orderNo']} label='Sıra'>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const questionType = getFieldValue(['questions', questionField.name, 'questionType']);
                        if (questionType !== 'MULTIPLE_CHOICE') {
                          return null;
                        }

                        return (
                          <Form.List name={[questionField.name, 'options']}>
                            {(optionFields, { add: addOption, remove: removeOption }) => (
                              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                                <Text strong>Şıklar</Text>
                                {optionFields.map((optionField) => (
                                  <Row key={optionField.key} gutter={8} align='middle'>
                                    <Col span={12}>
                                      <Form.Item
                                        name={[optionField.name, 'optionText']}
                                        rules={[{ required: true, message: 'Şık metni zorunludur' }]}
                                      >
                                        <Input placeholder='Şık metni' />
                                      </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                      <Form.Item name={[optionField.name, 'isOther']} valuePropName='checked'>
                                        <Switch checkedChildren='isOther' unCheckedChildren='Normal' />
                                      </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                      <Form.Item name={[optionField.name, 'orderNo']}>
                                        <InputNumber min={0} placeholder='Sıra' style={{ width: '100%' }} />
                                      </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                      <Button danger onClick={() => removeOption(optionField.name)}>
                                        Sil
                                      </Button>
                                    </Col>
                                  </Row>
                                ))}
                                <Button type='dashed' onClick={() => addOption({ optionText: '', isOther: false })}>
                                  Şık Ekle
                                </Button>
                              </Space>
                            )}
                          </Form.List>
                        );
                      }}
                    </Form.Item>
                  </Card>
                ))}

                <Button
                  type='dashed'
                  onClick={() =>
                    addQuestion({
                      questionText: '',
                      questionType: 'TEXT',
                      required: true,
                      options: [],
                    })
                  }
                >
                  Soru Ekle
                </Button>
              </Space>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title='Anket Gönderimi Ekle'
        open={isCampaignModalOpen}
        onCancel={closeCampaignModal}
        onOk={handleCreateCampaign}
        okText='Kaydet'
        width={1100}
        okButtonProps={{ loading: creatingCampaign }}
      >
        <Form layout='vertical' form={campaignForm} initialValues={{ delayMinutes: 0, contactIds: [] }}>
          <Form.Item name='name' label='Ad' rules={[{ required: true, message: 'Ad zorunludur' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='surveyId' label='Anket' rules={[{ required: true, message: 'Anket seçiniz' }]}>
            <Select placeholder='Anket seçiniz' options={activeSurveys.map((survey) => ({ label: survey.name, value: survey.id }))} />
          </Form.Item>
          <Form.Item name='templateId' label='Şablon' rules={[{ required: true, message: 'Şablon seçiniz' }]}>
            <Select placeholder='Şablon seçiniz' options={activeTemplates.map((template) => ({ label: template.name, value: template.id }))} />
          </Form.Item>

          <Form.Item name='contactIds' rules={[{ required: true, message: 'En az bir kişi seçiniz' }]} hidden>
            <Select mode='multiple' options={[]} />
          </Form.Item>

          <Form.Item label='Kişiler (Aktif)'>
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Space wrap>
                <Input.Search
                  allowClear
                  style={{ width: 320 }}
                  placeholder='Ad, soyad, telefon, e-posta, firma ara'
                  value={campaignContactSearch}
                  onChange={(event) => setCampaignContactSearch(event.target.value)}
                />
                <Select<ContactSourceFilter>
                  style={{ width: 160 }}
                  value={campaignContactSourceFilter}
                  onChange={setCampaignContactSourceFilter}
                  options={[
                    { label: 'Kaynak: Tümü', value: 'ALL' },
                    { label: 'Manuel', value: 'MANUAL' },
                    { label: 'İçe Aktarım', value: 'IMPORT' },
                  ]}
                />
                <Select
                  mode='multiple'
                  allowClear
                  style={{ minWidth: 240 }}
                  value={campaignTagFilters}
                  onChange={setCampaignTagFilters}
                  placeholder='Tag filtrele'
                  options={campaignTagOptions.map((tag) => ({ label: tag, value: tag }))}
                />
                <Text type='secondary'>Seçili kişi: {(selectedCampaignContactIds || []).length}</Text>
              </Space>

              <Table<MailingContact>
                rowKey='id'
                size='small'
                dataSource={filteredCampaignContacts}
                pagination={{ pageSize: 8 }}
                scroll={{ y: 280 }}
                rowSelection={{
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selectedCampaignContactIds || [],
                  onChange: (selectedRowKeys) => {
                    campaignForm.setFieldsValue({ contactIds: selectedRowKeys as string[] });
                  },
                }}
                columns={[
                  {
                    title: 'Ad Soyad',
                    key: 'name',
                    render: (_: unknown, record) => `${record.firstName || ''} ${record.lastName || ''}`.trim() || '-',
                  },
                  { title: 'E-posta', dataIndex: 'email', key: 'email' },
                  { title: 'Telefon', dataIndex: 'phone', key: 'phone', render: (value?: string | null) => value || '-' },
                  { title: 'Firma', dataIndex: 'companyName', key: 'companyName', render: (value?: string | null) => value || '-' },
                  {
                    title: 'Kaynak',
                    dataIndex: 'source',
                    key: 'source',
                    render: (source?: string | null) =>
                      source === 'IMPORT' ? <Tag color='processing'>{getMailingSourceLabel(source)}</Tag> : <Tag>{getMailingSourceLabel(source)}</Tag>,
                  },
                  {
                    title: 'Tag',
                    dataIndex: 'tags',
                    key: 'tags',
                    render: (tags?: string[] | null) =>
                      Array.isArray(tags) && tags.length > 0 ? (
                        <Space size={4} wrap>
                          {tags.map((tag) => (
                            <Tag key={tag}>{tag}</Tag>
                          ))}
                        </Space>
                      ) : (
                        '-'
                      ),
                  },
                ]}
              />
            </Space>
          </Form.Item>

          <Form.Item name='delayMinutes' label='Gecikme (dakika)' rules={[{ required: true, message: 'Gecikme zorunludur' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={startCampaignTarget ? `${startCampaignTarget.name} - Anket Gönderimi Başlat` : 'Anket Gönderimi Başlat'}
        open={isStartModalOpen}
        onCancel={closeStartModal}
        onOk={handleStartCampaign}
        okText='Başlat'
      >
        <Form layout='vertical' form={startForm}>
          <Form.Item name='startDate' label='Başlangıç Tarihi' rules={[{ required: true, message: 'Başlangıç tarihi seçiniz' }]}>
            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' />
          </Form.Item>
          <Form.Item
            name='sendWindow'
            label='Saat Aralığı'
            rules={[
              { required: true, message: 'Saat aralığı seçiniz' },
              {
                validator: (_, value: [dayjs.Dayjs, dayjs.Dayjs] | undefined) => {
                  if (!value || value.length !== 2) {
                    return Promise.reject(new Error('Saat aralığı seçiniz'));
                  }
                  if (!value[0].isBefore(value[1])) {
                    return Promise.reject(new Error('Başlangıç saati bitiş saatinden önce olmalıdır'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker.RangePicker format='HH:mm' minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name='responseDeadlineAt'
            label='Yanıt Son Tarihi'
            rules={[{ required: true, message: 'Yanıt son tarihi zorunludur' }]}
          >
            <DatePicker showTime format='DD.MM.YYYY HH:mm' style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={reportCampaign ? `${reportCampaign.name} - Anket Raporu` : 'Anket Raporu'}
        placement='right'
        width={980}
        open={isReportDrawerOpen}
        onClose={closeReportDrawer}
      >
        {reportCampaign && (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Descriptions bordered size='small' column={1}>
              <Descriptions.Item label='Durum'>
                <Tag color={campaignStatusColor(reportCampaign.status)}>{getCampaignStatusLabel(reportCampaign.status)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label='Anket'>{reportCampaign.survey?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label='Şablon'>{reportCampaign.template?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label='Planlanan Başlangıç'>{formatDateTime(reportCampaign.scheduledStartAt)}</Descriptions.Item>
              <Descriptions.Item label='Saat Aralığı'>
                {formatTimeRange(reportCampaign.sendWindowStartTime, reportCampaign.sendWindowEndTime)}
              </Descriptions.Item>
              <Descriptions.Item label='Planlanan Bitiş'>{formatDateTime(reportCampaign.plannedEndAt)}</Descriptions.Item>
              <Descriptions.Item label='Yanıt Son Tarihi'>{formatDateTime(reportCampaign.responseDeadlineAt)}</Descriptions.Item>
            </Descriptions>

            {reportSummary && (
              <Card size='small' loading={reportLoading}>
                <Row gutter={12}>
                  <Col span={6}>
                    <Statistic title='Toplam Alıcı' value={reportSummary.totalRecipients} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Gönderildi' value={reportSummary.sentCount} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Hatalı' value={reportSummary.failedCount} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Atlandı' value={reportSummary.skippedCount} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Bekleyen' value={reportSummary.pendingCount} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Yanıtlayan' value={reportSummary.answeredCount} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Yanıtlamayan' value={reportSummary.unansweredCount} />
                  </Col>
                  <Col span={6}>
                    <Statistic title='Yanıt Oranı' value={reportSummary.answerRate} precision={2} suffix='%' />
                  </Col>
                </Row>
              </Card>
            )}

            <Card size='small' title='Soru Analizi' loading={reportLoading}>
              <Table<MailingSurveyQuestionReport>
                rowKey='questionId'
                dataSource={questionReport}
                pagination={{ pageSize: 5 }}
                columns={[
                  { title: 'Soru', dataIndex: 'questionText', key: 'questionText' },
                  { title: 'Tip', dataIndex: 'questionType', key: 'questionType' },
                  { title: 'Toplam Cevap', dataIndex: 'totalAnswers', key: 'totalAnswers' },
                  { title: 'Other Text', dataIndex: 'otherTextCount', key: 'otherTextCount' },
                  {
                    title: 'Seçenek Dağılımı',
                    key: 'options',
                    render: (_: unknown, record) =>
                      record.options?.length
                        ? record.options.map((option) => `${option.optionText}: ${option.count}`).join(' | ')
                        : '-',
                  },
                ]}
              />
            </Card>

            <Card size='small' title='Alıcı Bazlı Durum' loading={reportLoading}>
              <Table<MailingSurveyRecipientReport>
                rowKey='recipientId'
                dataSource={recipientReport}
                pagination={{ pageSize: 8 }}
                columns={[
                  {
                    title: 'Kişi',
                    key: 'name',
                    render: (_: unknown, record) => `${record.firstName || ''} ${record.lastName || ''}`.trim() || '-',
                  },
                  { title: 'E-posta', dataIndex: 'email', key: 'email' },
                  {
                    title: 'Gönderim Durumu',
                    dataIndex: 'sendStatus',
                    key: 'sendStatus',
                    render: (status: string) => {
                      const color = status === 'SENT' ? 'success' : status === 'FAILED' ? 'error' : status === 'SKIPPED' ? 'warning' : 'default';
                      return <Tag color={color}>{getMailingLogStatusLabel(status)}</Tag>;
                    },
                  },
                  {
                    title: 'Yanıt',
                    dataIndex: 'answeredAt',
                    key: 'answeredAt',
                    render: (v?: string | null) => (v ? <Tag color='success'>YANITLANDI</Tag> : <Tag>YANITSIZ</Tag>),
                  },
                  {
                    title: 'Gönderim Zamanı',
                    dataIndex: 'sentAt',
                    key: 'sentAt',
                    render: (v?: string | null) => formatDateTime(v),
                  },
                  {
                    title: 'Yanıt Zamanı',
                    dataIndex: 'answeredAt',
                    key: 'answeredAtTime',
                    render: (v?: string | null) => formatDateTime(v),
                  },
                  {
                    title: 'Hata',
                    dataIndex: 'errorMessage',
                    key: 'errorMessage',
                    render: (v?: string | null) => v || '-',
                  },
                ]}
              />
            </Card>
          </Space>
        )}
      </Drawer>
    </Space>
  );
};

export default SurveyAdmin;
